import { useId, useRef, useState, type KeyboardEvent } from 'react';
import { cva } from 'class-variance-authority';
import { Flex, Icon, Text } from '@nula/core-ui';
import {
  MAX_TICKED_SOURCES,
  type TemplateSourceOption,
  type TemplateSourceOptionKind,
} from '../utils/template-sources';
import styles from './surface-sources-list.module.css';

export const SOURCE_KIND_ICON: Record<TemplateSourceOptionKind, string> = {
  page: 'description',
  document: 'description',
  surface: 'edit_note',
  recording: 'graphic_eq',
  passage: 'format_quote',
  event: 'event',
};

interface SurfaceSourcesListProps {
  options: readonly TemplateSourceOption[];
  ticked: readonly string[];
  limit?: number;
  onToggle: (id: string) => void;
  /** Ends the list with an Add a document… row. */
  onAdd?: () => void;
}

const row = cva(styles.row, {
  variants: {
    on: { true: styles.on },
    held: { true: styles.held },
  },
});

export function SurfaceSourcesList({
  options, ticked, limit = MAX_TICKED_SOURCES, onToggle, onAdd,
}: SurfaceSourcesListProps) {
  const hintId = useId();
  const atLimit = ticked.length >= limit;
  const rowRefs = useRef<(HTMLElement | null)[]>([]);
  const [focusIndex, setFocusIndex] = useState(() =>
    Math.max(0, options.findIndex((o) => ticked.includes(o.id))));

  const moveTo = (index: number) => {
    const next = Math.min(options.length - 1, Math.max(0, index));
    setFocusIndex(next);
    rowRefs.current[next]?.focus();
  };

  const handleKeyDown = (e: KeyboardEvent, index: number, id: string, held: boolean) => {
    if (e.key === 'ArrowDown') moveTo(index + 1);
    else if (e.key === 'ArrowUp') moveTo(index - 1);
    else if (e.key === 'Home') moveTo(0);
    else if (e.key === 'End') moveTo(options.length - 1);
    else if (e.key === ' ') {
      if (!held) onToggle(id);
    }
    else return;
    e.preventDefault();
    e.stopPropagation();
  };

  const add = () => {
    if (!atLimit) onAdd?.();
  };

  const handleAddKeyDown = (e: KeyboardEvent) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    e.preventDefault();
    e.stopPropagation();
    add();
  };

  return (
    <Flex vertical className={styles.wrap}>
      <Flex
        vertical
        role="group"
        aria-label="Sources"
        aria-describedby={atLimit ? hintId : undefined}
        className={styles.list}
      >
        {options.map((option, index) => {
          const on = ticked.includes(option.id);
          const held = atLimit && !on;
          return (
            <Flex
              key={option.id}
              ref={(el: HTMLElement | null) => {
                rowRefs.current[index] = el;
              }}
              role="checkbox"
              aria-checked={on}
              aria-disabled={held || undefined}
              tabIndex={index === focusIndex ? 0 : -1}
              align="center"
              className={row({ on, held })}
              onClick={() => {
                setFocusIndex(index);
                if (!held) onToggle(option.id);
              }}
              onKeyDown={(e: KeyboardEvent) => handleKeyDown(e, index, option.id, held)}
            >
              <Icon
                name={on ? 'check_box' : 'check_box_outline_blank'}
                size={16}
                className={styles.box}
              />
              <Icon name={SOURCE_KIND_ICON[option.kind]} size={16} className={styles.icon} />
              <Text className={styles.name}>{option.name}</Text>
              <Text className={styles.kind}>{option.kindLabel}</Text>
            </Flex>
          );
        })}
        {onAdd && (
          <Flex
            role="button"
            aria-disabled={atLimit || undefined}
            tabIndex={0}
            align="center"
            className={row({ held: atLimit })}
            onClick={add}
            onKeyDown={handleAddKeyDown}
          >
            <Icon name="add" size={16} className={styles.icon} />
            <Text className={styles.name}>Add a document…</Text>
          </Flex>
        )}
      </Flex>
      {atLimit && (
        <Text id={hintId} className={styles.hint}>{`Up to ${limit} sources`}</Text>
      )}
    </Flex>
  );
}
